// src/theme/brand.ts
//
// The single source of brand truth. Every colour in the product — the MUI
// palette, the ECharts/Plotly themes, the Monaco editor, the code panels —
// resolves back to a step in one of the ramps below. Nothing else may name a
// raw colour (see the `no-restricted-syntax` gate in eslint.config.js).
//
// ─── Derivation ──────────────────────────────────────────────────────────────
//
// The four anchors are sampled directly from the Fintela mark
// (fintela-minimal-logo.png). Measured in OKLCH:
//
//   gold    #EFC03C   L .828  C .151  H  88.2   outer chevron, apex
//   crimson #E53540   L .606  C .211  H  23.3   inner chevron, apex
//   blue    #2F6395   L .488  C .098  H 249.7   inner chevron, right tip
//   indigo  #383959   L .359  C .055  H 281.9   inner chevron, left tip
//
// Each ramp holds those hue angles constant and walks lightness on a fixed
// curve, with chroma scaled by a bell so the mid steps carry the most colour
// and the ends stay usable as tints/shades. Chroma maxima are deliberately
// LOW — the logo's own blue sits at C .098, and an institutional surface reads
// wrong the moment its colours get vivid. The `600` step lands on the anchor:
// blue-600 #3674AE and blue-700 #265E92 straddle the logo's #2F6395, and
// indigo-900 #323256 is the logo's #383959.
//
// ─── Why blue is primary and gold is not ─────────────────────────────────────
//
// The logo's gold measures 1.85:1 against white. WCAG needs 4.5:1 for text and
// 3:1 for boundaries, so gold can never carry a link, a label, or an outline in
// light mode. Against a dark surface it measures 10.19:1. The logo's blue
// measures 6.29:1 on white and works in both modes, so it takes `primary`;
// gold becomes the brand accent, applied structurally (see `accent` below).
//
// ─── Why there is no separate amber ──────────────────────────────────────────
//
// An independent amber for `warning` (H 58) was measured against the gold
// accent (H 88): ΔE 6.5 in normal vision and ΔE 0.6 under deuteranopia —
// indistinguishable. Shipping both as functional colours would be a defect, so
// `warning` and the brand accent share the `gold` ramp and are separated by
// FORM instead of hue:
//
//   accent   a structural marker — a 2px rule, an active-tab underline, the
//            mark itself. Never a filled pill.
//   warning  a filled status — gold-100 ground, gold-700 ink, always shipped
//            with an icon and a text label.
//
// Nothing in the product may encode state by colour alone. success-600 and
// primary-600 sit at ΔE 2.9 under deuteranopia (the classic green/blue
// collision), so P&L direction always carries a sign and an arrow glyph too.
//
// ─── Green ───────────────────────────────────────────────────────────────────
//
// The mark has no green. H 158 at low chroma was chosen to sit beside the blue
// rather than compete with it — a market-convention green that still reads as
// part of this family rather than a stock success colour bolted on.

/** A 10-step ramp, 50 (lightest) → 900 (darkest). */
export type Ramp = Readonly<Record<RampStep, string>>;
export type RampStep = 50 | 100 | 200 | 300 | 400 | 500 | 600 | 700 | 800 | 900;

/** Primary. Hue 249.7 — the logo's steel blue. */
export const blue: Ramp = {
  50: '#F1F8FF', 100: '#E0EFFF', 200: '#BFDDFC', 300: '#99C4F0', 400: '#71A8E0',
  500: '#4E8CC8', 600: '#3674AE', 700: '#265E92', 800: '#1D4A75', 900: '#173959',
} as const;

/** Brand accent AND `warning` — they are one ramp on purpose (see header). Hue 88.2. */
export const gold: Ramp = {
  50: '#FCF6E8', 100: '#F7ECD1', 200: '#ECD7A5', 300: '#DABC70', 400: '#C59E2F',
  500: '#A78203', 600: '#8B6B01', 700: '#715702', 800: '#5A4401', 900: '#453401',
} as const;

/** `error` and negative P&L. Hue 23.3 — the logo's crimson. */
export const red: Ramp = {
  50: '#FEF4F3', 100: '#FFE6E4', 200: '#FFCAC6', 300: '#FCA49F', 400: '#EB7E7A',
  500: '#D35D5B', 600: '#B74444', 700: '#993234', 800: '#7B2628', 900: '#5E1E1F',
} as const;

/** `success` and positive P&L. Hue 158 — chosen to harmonise, not to compete. */
export const green: Ramp = {
  50: '#EFFAF3', 100: '#DEF3E6', 200: '#BDE4CC', 300: '#95CFAD', 400: '#6AB68B',
  500: '#449C6E', 600: '#288357', 700: '#166B44', 800: '#105535', 900: '#0F4129',
} as const;

/** Cool structural counterweight. Hue 281.9 — the logo's deep indigo. */
export const indigo: Ramp = {
  50: '#F5F6FF', 100: '#E9EBFE', 200: '#D3D6F9', 300: '#B7BAEC', 400: '#9A9CDA',
  500: '#7E80C3', 600: '#6768A9', 700: '#53538D', 800: '#414270', 900: '#323256',
} as const;

/**
 * Surfaces, borders and ink. Carries a trace of the indigo hue (C .014) rather
 * than being a true grey, so neutrals sit under the brand instead of beside it.
 * Extended past the 10-step shape because dark mode needs the room: 900 is the
 * dark paper, 975 the dark canvas.
 */
export const neutral = {
  0: '#FFFFFF', 50: '#F9FAFE', 100: '#F3F3F7', 150: '#E8E9EF', 200: '#DBDCE3',
  300: '#BCBDC5', 400: '#9797A1', 500: '#797983', 600: '#5F6068', 700: '#46474F',
  800: '#2F3037', 850: '#202128', 900: '#15151C', 950: '#0C0D12', 975: '#07070B',
} as const;

/**
 * The literal colours of the mark, for the mark itself and for the brand
 * gradient. These are the sampled pixels, NOT ramp steps — do not use them as
 * UI colours. `logoGold` in particular is 1.85:1 on white.
 */
export const mark = {
  gold: '#EFC03C',
  crimson: '#E53540',
  blue: '#2F6395',
  indigo: '#383959',
} as const;

/**
 * The mark's own gradient, apex to tips. Used for brand moments only — the
 * Fintelligent surfaces and the login page. It replaced an off-brand
 * purple/pink/amber gradient that belonged to no part of the identity.
 */
export const brandGradient =
  `linear-gradient(135deg, ${mark.gold} 0%, ${mark.crimson} 45%, ${mark.blue} 100%)`;

/**
 * Full-bleed gradient grounds for the five Home workflow steps, in pipeline
 * order, each carrying white ink.
 *
 * NOT {@link brandGradient}: that one is the mark's own colours, and its gold
 * apex measures 1.71:1 against white — a title laid over it would be unreadable
 * for the first third of the card. It is also reserved for brand moments (login,
 * Fintelligent), which a row of dashboard tiles is not.
 *
 * These walk the two STRUCTURAL ramps instead, blue → indigo, deepening left to
 * right so the row reads as a progression. Structural, not functional, is the
 * point: `green` and `red` are P&L direction in this product, and five
 * navigation tiles must not look like status. Every stop below clears 4.5:1 on
 * white — the lightest, blue-600, sits at 4.92:1 — which `brand.test.ts` locks
 * in, because the ink is white at every card size and a darker step is always
 * available if a ramp is ever retuned.
 */
export const workflowStepGradients: readonly string[] = [
  `linear-gradient(135deg, ${blue[600]} 0%, ${blue[800]} 100%)`,
  `linear-gradient(135deg, ${blue[700]} 0%, ${blue[900]} 100%)`,
  `linear-gradient(135deg, ${indigo[600]} 0%, ${indigo[800]} 100%)`,
  `linear-gradient(135deg, ${indigo[700]} 0%, ${indigo[900]} 100%)`,
  `linear-gradient(135deg, ${indigo[800]} 0%, ${neutral[900]} 100%)`,
] as const;

/** The stops above, flat — what the contrast test measures. */
export const workflowStepGradientStops: readonly string[] = [
  blue[600], blue[800], blue[700], blue[900],
  indigo[600], indigo[800], indigo[700], indigo[900],
  indigo[800], neutral[900],
] as const;

/**
 * Categorical series colours, stepped separately per mode.
 *
 * A palette cannot be optimal against both #FFFFFF and #07070B, and the
 * previous single shared array was a real defect: half its steps failed the
 * lightness band in one mode or the other. Each array below passes all six
 * checks of `dataviz/scripts/validate_palette.js` against its own surface —
 * lightness band, chroma floor, adjacent CVD separation, the normal-vision
 * floor, and contrast.
 *
 * Order is FIXED and hues are never cycled or generated. A 9th series folds
 * into "Other", small multiples, or a composite encoding — it does not get a
 * new colour. Colour follows the entity, never its rank, so a filter that
 * changes the series count must not repaint the survivors.
 */
export const seriesLight = [
  '#4A8CC4', // blue    — lead series, the brand hue
  '#D9A82E', // gold
  '#B03246', // crimson
  '#1F9C93', // teal
  '#6560A8', // indigo
  '#5C8A33', // green
  '#A85F9E', // plum
  '#9C5518', // ochre
] as const;

export const seriesDark = [
  '#5392D1', '#B58E18', '#C35252', '#009C9A',
  // The plum/ochre pair sat at ΔE 6.0 under dichromacy — right on the floor.
  // Their lightness gap was widened (L .645 vs .520) rather than the threshold
  // being lowered, which is what `brand.test.ts` caught.
  '#706FC2', '#499557', '#B870B2', '#9E511E',
] as const;

export type Mode = 'light' | 'dark';

/** The categorical series palette for a mode. */
export function series(mode: Mode): readonly string[] {
  return mode === 'dark' ? seriesDark : seriesLight;
}
